import { api } from "@/lib/api"
import type {
	COUPON_DISCOUNT,
	FEE_GRID_RESPONSE,
	FEE_ITEM,
	PAYMENT_PLAN,
} from "../types/form-types"
import { useQuery } from "@tanstack/react-query"
import { checkPaymentPlan } from "@/components/fields/payment/utils"

//real-fee data related fetching hooks
export function useFee() {
	//to get the list of all fee
	const useGetFeeGridByBatchId = (universityId: string, batchId: string) => {
		return useQuery({
			queryFn: async () => {
				const { data } = await api.get<FEE_ITEM[]>(
					`/universities/${universityId}/batches/${batchId}/fee/builder`,
				)

				return data
			},
			queryKey: ["fee-grid", universityId, batchId],
			enabled: !!universityId && !!batchId,
		})
	}

	//to fetch the total price & discounts of payment plans
	const useGetPaymentPlanPrices = ({
		universityId,
		batchId,
		applicationId,
		programId,
		specilizationId,
		nationalityId,
		periodNumber = 1,
		componentIds,
		usedComponentIds,
		isRenewal = true,
	}: {
		universityId: string
		batchId: string
		applicationId: string
		programId: string
		specilizationId: string
		nationalityId: string
		periodNumber: number
		componentIds: string[]
		usedComponentIds: string[]
		isRenewal: boolean
	}) => {
		return useQuery({
			queryFn: async () => {
				if (
					!universityId ||
					!batchId ||
					!applicationId ||
					!programId ||
					!specilizationId ||
					!nationalityId
				) {
					return {
						course_id: "",
						components: [],
						discounts: [],
						is_last_semester: false,
					}
				}
				const params = new URLSearchParams({
					university_id: universityId,
					batch_id: batchId,
					program_id: programId,
					specialization_id: specilizationId,
					nationality_id: nationalityId,
					application_id: applicationId,
					period_number: periodNumber.toString(),
				})

				componentIds.forEach(id => params.append("component_ids", id))
				usedComponentIds.forEach(id =>
					params.append("used_component_ids", id),
				)

				let url = `universities/${universityId}/batches/${batchId}/applications/${applicationId}/fee-plan?${params.toString()}`
				if (isRenewal) {
					url = `universities/${universityId}/batches/${batchId}/applications/${applicationId}/renewal/fee-plan?${params.toString()}`
				}

				const { data } = await api.get<FEE_GRID_RESPONSE>(url)

				return data
			},
			queryKey: [
				"get-payment-plan-prices",
				universityId,
				batchId,
				applicationId,
				programId,
				specilizationId,
				nationalityId,
				usedComponentIds,
				componentIds,
			],
			enabled:
				!!universityId &&
				!!batchId &&
				!!applicationId &&
				!!programId &&
				!!specilizationId &&
				!!nationalityId,
		})
	}

	//to get fee grid for a specific selected payment plan
	const useGetFeeGridByApplicationId = ({
		universityId,
		batchId,
		applicationId,
		programId,
		specilizationId,
		nationalityId,
		periodNumber = 1,
		componentIds,
		usedComponentIds,
		paymentType = "self",
		isRenewal = true,
		selectedPaymentPlan,
	}: {
		universityId: string
		batchId: string
		applicationId: string
		programId: string
		specilizationId: string
		nationalityId: string
		periodNumber: number
		componentIds: string[]
		usedComponentIds: string[]
		paymentType?: string
		isRenewal: boolean
		selectedPaymentPlan: string
	}) => {
		return useQuery({
			queryFn: async () => {
				// if (
				// 	!universityId ||
				// 	!batchId ||
				// 	!applicationId ||
				// 	!programId ||
				// 	!specilizationId ||
				// 	!nationalityId
				// ) {
				// 	return undefined
				// }
				const params = new URLSearchParams({
					university_id: universityId,
					batch_id: batchId,
					program_id: programId,
					specialization_id: specilizationId,
					nationality_id: nationalityId,
					application_id: applicationId,
					period_number: periodNumber.toString(), //check once
					//only include selected_plan when isRenewal flag is true
					...(isRenewal
						? { selected_plan: selectedPaymentPlan }
						: {}),
				})

				componentIds.forEach(id => params.append("component_ids", id))
				usedComponentIds.forEach(id =>
					params.append("used_component_ids", id),
				)

				let url = `universities/${universityId}/batches/${batchId}/applications/${applicationId}/fee-summary?${params.toString()}`
				if (isRenewal) {
					url = `universities/${universityId}/batches/${batchId}/applications/${applicationId}/renewal/fee-summary?${params.toString()}`
				}

				const { data } = await api.get<FEE_GRID_RESPONSE>(url)

				return data
			},
			queryKey: [
				"get-fee-grid",
				universityId,
				batchId,
				applicationId,
				programId,
				specilizationId,
				nationalityId,
				componentIds,
				usedComponentIds,
				paymentType,
			],
			enabled:
				!!universityId &&
				!!batchId &&
				!!applicationId &&
				!!programId &&
				!!specilizationId &&
				!!nationalityId &&
				!!(isRenewal ? !!selectedPaymentPlan : true),
		})
	}

	const useDiscountedFee = ({
		feeComponents,
		couponDiscount,
		paymentType,
		lender_id,
		tenure_in_months,
		isRenewal,
		selectedPaymentPlan,
	}: {
		feeComponents: FEE_ITEM[]
		couponDiscount?: COUPON_DISCOUNT
		paymentType?: "self" | "loan"
		lender_id?: string
		tenure_in_months?: number
		isRenewal: boolean
		selectedPaymentPlan?: string
	}) => {
		// Step 1: Build a quick lookup map for discounts
		// const discountMap = new Map(
		// 	discountComponents.map(d => [d.component_id, d]),
		// )

		// Step 2: Compute final fees with discount applied
		let finalFees = feeComponents.map(fee => {
			// const discountItem = discountMap.get(fee.component_id)
			// let discountAmount = 0

			// if (discountItem) {
			// 	if (discountItem.type === "FIXED") {
			// 		discountAmount = discountItem.discount
			// 	} else if (discountItem.type === "PERCENTAGE") {
			// 		discountAmount = (fee.price * discountItem.discount) / 100
			// 	}
			// }

			// const finalPrice = fee.price - discountAmount

			// return { ...fee, discount: discountAmount, finalPrice }

			const price = fee.price_details.reduce(
				(sum, price_item) => sum + price_item.price,
				0,
			)

			const discount = fee.price_details.reduce(
				(sum, price_item) => sum + price_item.discount,
				0,
			)

			const finalPrice = fee.price_details.reduce(
				(sum, price_item) => sum + price_item.final_price,
				0,
			)

			// If backend already provides final price, use that
			// const finalPrice = Math.floor(discount ?? price)
			// Optionally compute discount amount if needed for UI display
			// const discountAmount = fee.price - finalPrice

			return {
				...fee,
				price,
				discount,
				finalPrice: Math.floor(finalPrice),
			}
		})

		// Apply coupon discount (if available)
		if (couponDiscount && paymentType) {
			finalFees = finalFees.map(fee => {
				const shouldApply =
					fee.is_course_component &&
					checkPaymentPlan(
						fee.frequency as PAYMENT_PLAN,
						couponDiscount,
						paymentType,
						lender_id,
						tenure_in_months,
					)

				if (!shouldApply) return fee

				const couponAmount = Math.floor(
					couponDiscount.discount_type === "PERCENTAGE"
						? (fee.finalPrice * couponDiscount.discount) / 100
						: couponDiscount.discount,
				)

				const newFinalPrice = Math.max(
					(fee.finalPrice ?? fee.price) - couponAmount,
					0,
				)

				return {
					...fee,
					coupon_discount: couponAmount,
					finalPrice: newFinalPrice,
				}
			})
		}

		if (isRenewal && couponDiscount && paymentType && selectedPaymentPlan) {
			//add the fee of all the course fee component and make a new fee item
			const courseFee = finalFees
				.filter(fee => fee.is_course_component)
				.reduce((sum, fee) => sum + fee.finalPrice, 0)

			const newFeeItem = {
				component_id: selectedPaymentPlan.replace(/^(self|loan)-/, ""),
				name: "Discount on course fee",
				description: "Discount",
				is_course_component: true,
				finalPrice: courseFee,
				price: courseFee,
				discount: 0,
				revision_id: "",
				dimension_id: "",
				frequency: "ONE_TIME",
				nationality_id: "",
				pay_at: "",
				currency: feeComponents[0]?.currency || "INR",
				price_details: [],
				isDiscountComponent: true,
			}

			const shouldApply = checkPaymentPlan(
				newFeeItem.frequency as PAYMENT_PLAN,
				couponDiscount,
				paymentType,
				lender_id,
				tenure_in_months,
			)

			if (!shouldApply) return finalFees

			const couponAmount = Math.floor(
				couponDiscount.discount_type === "PERCENTAGE"
					? (newFeeItem.finalPrice * couponDiscount.discount) / 100
					: couponDiscount.discount,
			)

			const newFinalPrice = Math.max(couponAmount, 0)

			finalFees.push({
				...newFeeItem,
				coupon_discount: couponAmount,
				price: newFinalPrice,
				finalPrice: newFinalPrice,
			})
		}

		return finalFees
	}

	return {
		useGetFeeGridByBatchId,
		useGetFeeGridByApplicationId,
		useDiscountedFee,
		useGetPaymentPlanPrices,
	}
}
